/**
 * IVP (Industry Viability Profile) API Client
 *
 * Dedicated API client for IVP endpoints.
 * Reuses the same base URL and auth pattern as companyProfileApi.
 */

import axios, { AxiosInstance } from 'axios'
import type { IVPData, IVPVitalSign } from './companyProfileApi'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

export interface IVPCatalogEntry {
  id: number
  naics_code: string
  revenue_tier: string
  industry_label: string
  profile_version: number
  status: string
  confidence_score: number | null
  created_at: string
  updated_at: string
}

export interface NodeWeights {
  [nodeId: string]: {
    gravity_multiplier?: number
    priority_boost?: number
    causal_importance?: number
  }
}

class IVPApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
      timeout: 120000, // 2 min — IVP generation involves LLM calls
    })

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('jwt_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('jwt_token')
          window.location.href = 'http://127.0.0.1:5000'
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Get IVP linked to a company.
   */
  async getIVP(companyId: number): Promise<IVPData | null> {
    try {
      const response = await this.client.get<{ status: string; ivp: IVPData }>(
        `/ivp/${companyId}`
      )
      return response.data.ivp
    } catch (err: any) {
      if (err.response?.status === 404 || err.response?.status === 403) {
        return null
      }
      throw err
    }
  }

  /**
   * Trigger IVP generation.
   */
  async generateIVP(
    companyId: number,
    naicsCode?: string,
    revenueTier?: string
  ): Promise<{ ivp_id: number; ivp: IVPData; message: string }> {
    const response = await this.client.post(`/ivp/${companyId}/generate`, {
      naics_code: naicsCode || null,
      revenue_tier: revenueTier || null,
    })
    return response.data
  }

  /**
   * Get vital signs with company actuals.
   */
  async getVitalSigns(companyId: number): Promise<{ metrics: IVPVitalSign[]; ivp_id: number } | null> {
    try {
      const response = await this.client.get(`/ivp/${companyId}/vital-signs`)
      return response.data.vital_signs
    } catch {
      return null
    }
  }

  /**
   * Get node weight overlay.
   */
  async getNodeWeights(companyId: number): Promise<NodeWeights> {
    try {
      const response = await this.client.get<{ status: string; weights: NodeWeights }>(
        `/ivp/${companyId}/weights`
      )
      return response.data.weights
    } catch {
      return {}
    }
  }

  /**
   * Update an IVP section.
   */
  async updateSection(companyId: number, sectionName: string, data: Record<string, any>): Promise<void> {
    await this.client.put(`/ivp/${companyId}/section/${sectionName}`, { data })
  }

  /**
   * Get IVP catalog (all generated profiles).
   */
  async getCatalog(): Promise<IVPCatalogEntry[]> {
    try {
      const response = await this.client.get<{ catalog: IVPCatalogEntry[] }>('/ivp/catalog')
      return response.data.catalog
    } catch {
      return []
    }
  }
}

export const ivpApi = new IVPApiClient()
export default ivpApi
